import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Shield, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DealRoom() {
  const { t, language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    organization: '',
    position: '',
    country: '',
    investment_interest: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/deal-room/access`, { ...formData, language });
      toast.success(t('dealRoom.success'));
      setFormData({
        full_name: '',
        email: '',
        organization: '',
        position: '',
        country: '',
        investment_interest: '',
        message: '',
      });
    } catch (error) {
      toast.error(t('dealRoom.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-32">
      <section className="py-24 md:py-32" data-testid="dealroom-hero">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-400/30 rounded-full backdrop-blur-sm mb-6">
              <Lock className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-amber-300">
                {language === 'fr' && 'Accès Restreint'}
                {language === 'en' && 'Restricted Access'}
                {language === 'pt' && 'Acesso Restrito'}
                {language === 'it' && 'Accesso Riservato'}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('dealRoom.title')}
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
              {t('dealRoom.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {language === 'fr' && [
              { icon: Shield, title: 'Due Diligence', desc: 'Documentation complète' },
              { icon: TrendingUp, title: 'ROI Validé', desc: 'Projections financières' },
              { icon: Lock, title: 'Confidentiel', desc: 'Accès sécurisé' },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <item.icon className="w-10 h-10 text-sky-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
            {language === 'en' && [
              { icon: Shield, title: 'Due Diligence', desc: 'Complete documentation' },
              { icon: TrendingUp, title: 'Validated ROI', desc: 'Financial projections' },
              { icon: Lock, title: 'Confidential', desc: 'Secure access' },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <item.icon className="w-10 h-10 text-sky-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
            {language === 'pt' && [
              { icon: Shield, title: 'Due Diligence', desc: 'Documentação completa' },
              { icon: TrendingUp, title: 'ROI Validado', desc: 'Projeções financeiras' },
              { icon: Lock, title: 'Confidencial', desc: 'Acesso seguro' },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <item.icon className="w-10 h-10 text-sky-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
            {language === 'it' && [
              { icon: Shield, title: 'Due Diligence', desc: 'Documentazione completa' },
              { icon: TrendingUp, title: 'ROI Validato', desc: 'Proiezioni finanziarie' },
              { icon: Lock, title: 'Riservato', desc: 'Accesso sicuro' },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
                <item.icon className="w-10 h-10 text-sky-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-12">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3">
                {language === 'fr' && 'Demande d\'Accès'}
                {language === 'en' && 'Access Request'}
                {language === 'pt' && 'Solicitação de Acesso'}
                {language === 'it' && 'Richiesta di Accesso'}
              </h2>
              <p className="text-base text-slate-300">
                {t('dealRoom.description')}
              </p>
            </div>

            <form onSubmit={handleSubmit} data-testid="dealroom-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t('dealRoom.fullName')} *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    data-testid="dealroom-fullname-input"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t('dealRoom.email')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="dealroom-email-input"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t('dealRoom.organization')} *
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    data-testid="dealroom-organization-input"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t('dealRoom.position')} *
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    data-testid="dealroom-position-input"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t('dealRoom.country')} *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    data-testid="dealroom-country-input"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t('dealRoom.investmentInterest')} *
                  </label>
                  <input
                    type="text"
                    name="investment_interest"
                    value={formData.investment_interest}
                    onChange={handleChange}
                    data-testid="dealroom-investment-input"
                    placeholder={t('dealRoom.investmentPlaceholder')}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t('dealRoom.message')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  data-testid="dealroom-message-input"
                  rows="4"
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                data-testid="dealroom-submit-button"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-sky-600 text-white rounded-full font-medium hover:bg-sky-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    {language === 'fr' && 'Envoi en cours...'}
                    {language === 'en' && 'Sending...'}
                    {language === 'pt' && 'Enviando...'}
                    {language === 'it' && 'Invio in corso...'}
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    {t('dealRoom.submit')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-400">
            {language === 'fr' && 'Les demandes d\'accès sont examinées sous 48h ouvrées. Seuls les investisseurs institutionnels qualifiés recevront un accès à la Deal Room.'}
            {language === 'en' && 'Access requests are reviewed within 48 business hours. Only qualified institutional investors will receive access to the Deal Room.'}
            {language === 'pt' && 'Os pedidos de acesso são analisados em até 48 horas úteis. Apenas investidores institucionais qualificados receberão acesso à Deal Room.'}
            {language === 'it' && 'Le richieste di accesso vengono esaminate entro 48 ore lavorative. Solo gli investitori istituzionali qualificati riceveranno accesso alla Deal Room.'}
          </p>
        </div>
      </section>
    </div>
  );
}
